/**
 * CR-7058: shown in place of a page whose navigation entry has been switched
 * off in Platform Admin → Navigation Menu Management, or which the user's
 * tenant / module permissions do not include. Rendered inside MainLayout so
 * the sidebar stays usable and the user can move on.
 */
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Lock, ArrowLeft, Home } from 'lucide-react';

interface FeatureUnavailableProps {
  /** Display name of the switched-off page, e.g. "Part Schedule". */
  featureName: string;
  /** Where "Go to home" sends the user — the first page they may open. */
  homeHref: string;
}

export default function FeatureUnavailable({ featureName, homeHref }: FeatureUnavailableProps) {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="feature-unavailable">
      <Card className="max-w-lg w-full">
        <CardContent className="flex flex-col items-center text-center gap-4 py-10">
          <div className="rounded-full bg-muted p-4">
            <Lock className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
          </div>
          <h1 className="text-xl font-semibold">This feature is not available</h1>
          <p className="text-muted-foreground">
            The <span className="font-medium text-foreground">{featureName}</span> page is not
            enabled on this platform or for your account. If you believe you should have access,
            please contact your administrator.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go back
            </Button>
            <Button asChild>
              <Link to={homeHref}>
                <Home className="h-4 w-4 mr-2" />
                Go to home
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
